/**
 * Property formatting utilities
 * Helpers for formatting property data for display
 */

/**
 * Format currency value
 * @param {number} value - The value to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value) => {
  if (!value || isNaN(value)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format number with commas
 * @param {number} value - The value to format
 * @returns {string} - Formatted number string
 */
export const formatNumber = (value) => {
  if (!value || isNaN(value)) return "N/A";
  return new Intl.NumberFormat("en-US").format(value);
};

/**
 * Format percentage
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return "N/A";
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format square feet
 * @param {number} value - The value to format
 * @returns {string} - Formatted square feet string
 */
export const formatSquareFeet = (value) => {
  if (!value || value === 0) return "N/A";
  return `${formatNumber(value)} sq ft`;
};

/**
 * Format address string
 * @param {Object} property - The property object
 * @returns {string} - Formatted address
 */
export const formatAddress = (property) => {
  const parts = [];
  if (property?.streetAddress) parts.push(property.streetAddress);
  if (property?.city) parts.push(property.city);
  if (property?.state) parts.push(property.state);
  if (property?.zip) parts.push(property.zip);
  return parts.join(", ");
};

/**
 * Get location string
 * @param {Object} property - The property object
 * @returns {string} - Formatted location
 */
export const getLocationString = (property) => {
  const parts = [];
  if (property?.city) parts.push(property.city);
  if (property?.state) parts.push(property.state);
  if (property?.county) parts.push(`${property.county} County`);
  return parts.join(", ");
};

/**
 * Get property type display name
 * @param {Object} property - The property object
 * @returns {string} - Property type name
 */
export const getPropertyType = (property) => {
  return property?.propertyType || property?.landUse || "Residential";
};

/**
 * Get yes/no display value
 * @param {boolean} value - The boolean value
 * @returns {string} - "Yes" or "No"
 */
export const formatBoolean = (value) => {
  return value ? "Yes" : "No";
};

/**
 * Truncate text with ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

